import { Dropdown, Form } from 'react-bootstrap';
import React from 'react';
import { FilterDto } from '../../../types/FilterDto';
import { useTranslation } from 'react-i18next';

interface SelectFilterProps {
    filter: FilterDto;
    values: string[];
    onSelectChange: (name: string, value: string[], description: string) => void;
}

export default function AppHierarchicalMultiSelectFilter({
    filter,
    values,
    onSelectChange
}: SelectFilterProps) {
    const { t } = useTranslation(['common', 'dashboard']);
    const [search, setSearch] = React.useState('');  //

    const valuesStr = React.useMemo(
        () => values.map(v => v.toString()),
        [values]
    );

    const childrenMap = React.useMemo(() => {
        const map = new Map<string, string[]>();
        filter.values?.forEach(v => {
            const id = v.key?.toString();
            const parentId = (v.value as any)?.parentId?.toString();
            if (!id || !parentId) return;
            if (!map.has(parentId)) map.set(parentId, []);
            map.get(parentId)!.push(id);
        });
        return map;
    }, [filter.values]);

    const getAllChildren = React.useCallback(
        (id: string, acc = new Set<string>()) => {
            const children = childrenMap.get(id) ?? [];
            for (const child of children) {
                if (!acc.has(child)) {
                    acc.add(child);
                    getAllChildren(child, acc);
                }
            }
            return acc;
        },
        [childrenMap]
    );

    const handleToggle = (id: string) => {
        const updated = new Set(valuesStr);
        const isSelected = updated.has(id);
        const isChild = !!(filter.values?.find(v => v.key?.toString() === id)?.value as any)?.parentId;

        if (isChild) {
            const parentId = (filter.values?.find(v => v.key?.toString() === id)?.value as any)?.parentId?.toString();
            if (parentId && updated.has(parentId)) return;
        }

        if (isSelected) {
            updated.delete(id);
        } else {
            updated.add(id);
            getAllChildren(id).forEach(c => updated.delete(c));
        }

        onSelectChange(filter.name, Array.from(updated), filter.description);
    };

    //  Filtrar manteniendo padres si alg�n hijo coincide y viceversa
    const filteredValues = React.useMemo(() => {
        const all = filter.values?.filter(v => v.key != null) ?? [];
        if (!search.trim()) return all;

        const lower = search.toLowerCase();

        const matchingIds = new Set<string>();

        all.forEach(v => {
            const id = v.key!.toString();
            const label = t((v.value as any).description).toLowerCase();
            const isChild = !!(v.value as any)?.parentId;
            const parentId = (v.value as any)?.parentId?.toString();

            if (label.includes(lower)) {
                matchingIds.add(id);
                // Si es hijo, incluir tambi�n su padre
                if (isChild && parentId) matchingIds.add(parentId);
                // Si es padre, incluir tambi�n sus hijos
                if (!isChild) getAllChildren(id).forEach(c => matchingIds.add(c));
            }
        });

        return all.filter(v => matchingIds.has(v.key!.toString()));
    }, [search, filter.values, getAllChildren, t]);

    const renderItem = (value: any, index: number) => {
        const id = value.key!.toString();
        const isChild = !!(value.value as any)?.parentId;
        const parentId = (value.value as any)?.parentId?.toString();
        const label = t((value.value as any).description);
        const isDisabled = isChild && !!parentId && valuesStr.includes(parentId);

        return (
            <Dropdown.Item
                key={index}
                as={React.forwardRef<HTMLDivElement, any>(({ ...props }, ref) => (
                    <Form.Group
                        ref={ref}
                        className={`dropdown-item mb-0 ${isChild ? 'tree-child' : 'tree-parent'} ${isDisabled ? 'opacity-50' : ''}`}
                        controlId={`check-${id}`}
                        {...props}
                    >
                        <Form.Check
                            type="checkbox"
                            label={label}
                            checked={valuesStr.includes(id)}
                            onChange={() => handleToggle(id)}
                            className={isChild ? 'tree-child' : ''}
                            disabled={isDisabled}
                        />
                    </Form.Group>
                ))}
            />
        );
    };

    return (
        <Form.Group controlId={`form${filter.name}MultipleSelect`}>
            <Form.Label>{t(filter.description)}</Form.Label>

            <Dropdown autoClose="outside" drop="down">
                <Dropdown.Toggle variant="light" id={`dropdown-${filter.name}`}>
                    {t('dashboard:filters.selectDafaultTag', {
                        filter: t(filter.description)
                    })}
                </Dropdown.Toggle>

                <Dropdown.Menu
                    className="tree-dropdown-menu"
                    align="start"
                    renderOnMount
                    popperConfig={{ strategy: 'fixed' }}
                    style={{ maxHeight: 450, overflow: 'auto' }}
                >
                    {/* Buscador */}
                    <div className="px-2 pb-2" onKeyDown={e => e.stopPropagation()}>
                        <Form.Control
                            type="text"
                            placeholder={t('common:search')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>

                    <Dropdown.Divider />

                    {filteredValues.length > 0
                        ? filteredValues.map(renderItem)
                        : (
                            <Dropdown.Item disabled>
                                {t('common:noResults')}
                            </Dropdown.Item>
                        )
                    }
                </Dropdown.Menu>
            </Dropdown>
        </Form.Group>
    );
}